import { NodeProps } from '../../../../types.ts';
import CustomNode from './CustomNode.tsx';
import { Edge, Node, useEdges } from 'reactflow';
import { useMemo } from 'react';
import { EmbedWorkflowConfiguration, WorkflowNode } from '../../../../api/model.ts';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Tooltip } from '@mui/material';
import { useStore } from 'store/store.ts';

const EmbedWorkflowNode = (props: NodeProps) => {
  const edges = useEdges();

  const configuration = props.data.configuration as EmbedWorkflowConfiguration;
  const selectedWorkflow = useStore().selectedEmbedWorkflows?.find(
    (wf) => wf.id === configuration.workflow_id && wf.revision === configuration.revision,
  );

  const hasRequiredConnections = useMemo(() => {
    if (!selectedWorkflow || edges.length === 0) {
      return false;
    }
    const targetIds = new Set(selectedWorkflow.edges.map((edge) => edge.target));
    const inputNodes = selectedWorkflow.nodes.filter((node) => !targetIds.has(node.id)).map((node) => node.id);
    const outputNodes = edges.filter((edge) => edge.source === props.id).map((edge) => edge.target);

    const { input_node_mappings, output_node_mappings } = configuration;

    const connectedInputs = new Set(input_node_mappings?.map((mapping) => mapping.child_node_id) || []);
    const connectedOutputs = new Set(output_node_mappings?.map((mapping) => mapping.parent_node_id) || []);

    const atLeastOneInputConnected = inputNodes.some((inputNodeId) => connectedInputs.has(inputNodeId));
    const allOutputsConnected = outputNodes.every((consumerNodeId) => connectedOutputs.has(consumerNodeId));
    return atLeastOneInputConnected && allOutputsConnected;
  }, [selectedWorkflow, edges, props.id, configuration]);

  return (
    <div style={{ position: 'relative' }}>
      {!hasRequiredConnections && (
        <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 10000 }}>
          <Tooltip title="Missing required input/output connections" arrow>
            <LinkOffIcon style={{ color: 'red', fontSize: 25 }} />
          </Tooltip>
        </div>
      )}
      <CustomNode {...props} name={`${selectedWorkflow?.name} workflow`} />
    </div>
  );
};

export function updateEmbedWorkflowNodeConfig(node: Node<WorkflowNode>, edges: Edge[]): Node {
  const config = node.data.configuration as EmbedWorkflowConfiguration;

  const cleanedConfig = {
    ...config,
    input_node_mappings: (config.input_node_mappings ?? []).filter((mapping) =>
      edges.find((edge) => edge.source === mapping.parent_node_id && edge.target === node.id),
    ),
    output_node_mappings: (config.output_node_mappings ?? []).filter((mapping) =>
      edges.find((edge) => edge.target === mapping.parent_node_id && edge.source === node.id),
    ),
  };

  return {
    ...node,
    data: {
      ...node.data,
      configuration: cleanedConfig,
    },
  };
}

export default EmbedWorkflowNode;
